import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useLang } from '../context/LangContext';

export default function ResetPasswordPage() {
  const { t, lang, switchLang } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [state, setState]   = useState('checking'); // checking | valid | invalid | done
  const [info, setInfo]     = useState(null);
  const [pw, setPw]         = useState('');
  const [pw2, setPw2]       = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    api.get(`/api/auth/reset/${token}`)
      .then(r => { setInfo(r.data); setState('valid'); })
      .catch(() => setState('invalid'));
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (pw.length < 8) { setError(t('reset_too_short')); return; }
    if (pw !== pw2)    { setError(t('reset_mismatch')); return; }
    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, password: pw });
      setState('done');
    } catch (err) {
      setError(err.response?.data?.error || t('error'));
    } finally {
      setLoading(false);
    }
  }

  const isInvite = info?.purpose === 'invite';

  return (
    <div className="login-page">
      <div className="card shadow-sm" style={{ width: 380 }}>
        <div className="card-body p-4">
          <div className="d-flex justify-content-end mb-3">
            <div className="lang-toggle">
              <button className={lang === 'nl' ? 'active' : ''} onClick={() => switchLang('nl')}>NL</button>
              <button className={lang === 'en' ? 'active' : ''} onClick={() => switchLang('en')}>EN</button>
            </div>
          </div>
          <div className="text-center mb-4">
            <img src="/logo-fdconsult.png" alt="FDConsult" style={{ height: 56, maxWidth: '100%' }} />
            <div className="text-muted small mt-2 fw-medium" style={{ letterSpacing: '0.05em' }}>Audit Tool</div>
          </div>

          {state === 'checking' && (
            <div className="text-center text-muted py-3">
              <span className="spinner-border spinner-border-sm me-2" />{t('loading')}
            </div>
          )}

          {state === 'invalid' && (
            <>
              <div className="alert alert-danger py-2">{t('reset_invalid')}</div>
              <button className="btn btn-outline-secondary w-100" onClick={() => navigate('/login')}>
                {t('forgot_back')}
              </button>
            </>
          )}

          {state === 'done' && (
            <>
              <div className="alert alert-success py-2">{t('reset_success')}</div>
              <button className="btn btn-primary w-100" onClick={() => navigate('/login')}>
                {t('login_submit')}
              </button>
            </>
          )}

          {state === 'valid' && (
            <form onSubmit={handleSubmit}>
              <h6 className="mb-1">{isInvite ? t('reset_title_invite') : t('reset_title')}</h6>
              <div className="text-muted small mb-3">
                {isInvite ? t('reset_intro_invite') : t('reset_intro')}{info?.email ? ` (${info.email})` : ''}
              </div>
              {error && <div className="alert alert-danger py-2">{error}</div>}
              <div className="mb-3">
                <label className="form-label small fw-medium">{t('reset_password')}</label>
                <input type="password" className="form-control" value={pw}
                  onChange={e => setPw(e.target.value)} autoFocus required minLength={8} />
              </div>
              <div className="mb-3">
                <label className="form-label small fw-medium">{t('reset_password_confirm')}</label>
                <input type="password" className="form-control" value={pw2}
                  onChange={e => setPw2(e.target.value)} required minLength={8} />
              </div>
              <button type="submit" className="btn btn-primary w-100 mt-1" disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                {t('reset_submit')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
